<template>
  <div class="tts-dark-container">
    <div class="game-header">
      <div class="header-content">
        <h2>🎤 {{ $t('app.title') }}</h2>
        <div class="header-controls">
          <button class="theme-toggle" @click="toggleTheme">
            {{ isDarkMode ? '☀️' : '🌙' }} {{ currentLocale }}
          </button>
        </div>
      </div>
    </div>

      <div v-if="isFreeUser" class="free-user-notice">
        <p>🔒 {{ $t('app.characterLimit') }}: {{ maxCharsFree }}. 
        <router-link to="/pricing">💎 {{ $t('app.updatePlan') }}</router-link> {{ $t('app.forMore') }}</p>
      </div>

    <div class="text-input-section">
      <div class="input-container">
        <textarea 
          v-model="inputText" 
          :placeholder="$t('app.title')"
          :maxlength="maxChars"
          @input="updateCharCount"
          class="gamified-textarea"
        ></textarea>
        <div class="char-counter-wrapper">
          <div class="char-counter">
            {{ charCount }} / {{ maxChars }}
          </div>
          <div class="progress-ring">
            <svg width="40" height="40">
              <circle 
                :stroke="progressColor" 
                stroke-width="3" 
                fill="transparent" 
                r="18" 
                cx="20" 
                cy="20"
                :stroke-dasharray="113.097" 
                :stroke-dashoffset="113.097 - (113.097 * charCount / maxChars)"
              />
            </svg>
          </div>
        </div>
      </div>
      
      <!-- Selector de Idioma y Voz -->
      <div class="voice-selection-section">
        <h3>🗣️ {{ $t('app.voiceSelection') }}</h3>
        <div class="voice-controls">
          <div class="language-selector">
            <label>🌎 {{ $t('app.language') }}:</label>
            <select v-model="selectedLanguage" @change="filterVoicesByLanguage" class="gamified-select">
              <option v-for="lang in availableLanguages" :key="lang.code" :value="lang.code">
                {{ lang.name }} ({{ lang.nativeName }})
              </option>
            </select>
          </div>
          
          <div class="voice-selector">
            <label>🎤 {{ $t('app.voice') }}:</label>
            <select v-model="selectedVoice" class="gamified-select">
              <option v-for="voice in filteredVoices" :key="voice.name" :value="voice">
                {{ voice.name }} {{ voice.gender === 'male' ? '👨' : '👩' }}
              </option>
            </select>
            <button @click="playVoiceSample" class="sample-btn" :disabled="!selectedVoice">
              🔄 {{ $t('app.testVoice') }}
            </button>
          </div>
        </div>
        
        <div class="voice-preview" v-if="selectedVoice">
          <div class="voice-details">
            <h4>{{ $t('app.selectedVoice') }}: {{ selectedVoice.name }}</h4>
            <p>{{ $t('app.language') }}: {{ selectedVoice.lang }} | 
               {{ $t('app.gender.title') }}: {{ selectedVoice.gender === 'male' ?  $t('app.gender.male')  :  $t('app.gender.female')  }}</p>
          </div>
        </div>
      </div>
      
      <div class="advanced-controls">
      <div class="advanced-header" @click="toggleAdvanced">
        <h3>⚡ {{ $t('app.advancedSettings') }}</h3>
        <span class="toggle-icon">{{ showAdvanced ? '▼' : '▶' }}</span>
      </div>
      
      <transition name="slide">
        <div v-if="showAdvanced" class="advanced-content">
          <div class="controls-grid">
            <!-- Columna izquierda -->
            <div class="control-column">
              <div class="control-card">
                <h4>🎧 {{ $t('app.listenMode') }}</h4>
                <p class="control-description">{{ $t('app.listenModeDesc') }}</p>
                <div class="toggle-container">
                  <label class="toggle-switch">
                    <input type="checkbox" v-model="listenMode">
                    <span class="slider round"></span>
                  </label>
                  <span class="toggle-label">{{ listenMode ? 'Activado' : 'Desactivado' }}</span>
                </div>
              </div>
              
              <div class="control-card">
                <h4>🎵 {{ $t('app.bgm') }}</h4>
                <p class="control-description">{{ $t('app.bgmDesc') }}</p>
                <div v-if="!currentBGM" class="bgm-upload-area">
                  <input type="file" id="bgm-upload" @change="handleBGMUpload" accept="audio/*" hidden>
                  <label for="bgm-upload" class="upload-btn">📤 {{ $t('app.uploadBgm') }}</label>
                </div>
                <div v-else class="bgm-management">
                  <p>BGM actual: {{ currentBGM.name }}</p>
                  <div class="bgm-controls">
                    <button @click="playBGM" class="bgm-btn">▶️</button>
                    <button @click="stopBGM" class="bgm-btn">⏹️</button>
                    <button @click="removeBGM" class="bgm-btn">🗑️</button>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Columna central -->
            <div class="control-column">
              <div class="control-card">
                <h4>🗂️ {{ $t('app.audioFormat') }}</h4>
                <div class="format-grid">
                  <button v-for="format in audioFormats" 
                          :key="format" 
                          @click="selectedFormat = format"
                          :class="['format-btn', { active: selectedFormat === format }]">
                    {{ format }}
                  </button>
                </div>
              </div>
              
              <div class="control-card">
                <h4>🗃️ {{ $t('app.mp3Quality') }}</h4>
                <select v-model="mp3Quality" class="gamified-select">
                  <option v-for="quality in mp3Qualities" :key="quality.value" :value="quality.value">
                    {{ quality.label }}
                  </option>
                </select>
              </div>
            </div>
            
            <!-- Columna derecha -->
            <div class="control-column">
              <div class="control-card">
                <h4>🗣️ {{ $t('app.voiceSpeed') }}</h4>
                <div class="slider-container">
                  <input 
                    type="range" 
                    min="0.5" 
                    max="2" 
                    step="0.1" 
                    v-model="rate" 
                    class="gamified-slider"
                  >
                  <div class="slider-value">{{ rate }}x</div>
                </div>
              </div>
              
              <div class="control-card">
                <h4>📢 {{ $t('app.voiceVolume') }}</h4>
                <div class="slider-container">
                  <input 
                    type="range" 
                    min="0" 
                    max="1" 
                    step="0.1" 
                    v-model="volume" 
                    class="gamified-slider"
                  >
                  <div class="slider-value">{{ Math.round(volume * 100) }}%</div>
                </div>
              </div>
              
              <div class="control-card">
                <h4>🎚️ {{ $t('app.pitchAdjust') }}</h4>
                <div class="slider-container">
                  <input 
                    type="range" 
                    min="0.5" 
                    max="1.5" 
                    step="0.1" 
                    v-model="pitch" 
                    class="gamified-slider"
                  >
                  <div class="slider-value">{{ pitch === 1 ? 'Normal' : pitch > 1 ? 'Alto (' + pitch + ')' : 'Bajo (' + pitch + ')' }}</div>
                </div>
              </div>
              
              <div class="control-card">
                <h4>🕒 {{ $t('app.paragraphPause') }}</h4>
                <div class="slider-container">
                  <input 
                    type="range" 
                    min="0" 
                    max="2000" 
                    step="100" 
                    v-model="paragraphPause" 
                    class="gamified-slider"
                  >
                  <div class="slider-value">{{ paragraphPause }}ms</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
      
      <button 
        @click="convertToSpeech" 
        :disabled="!inputText || isConverting || charCount > maxChars || !selectedVoice"
        :class="['convert-btn', { 'converting': isConverting, 'disabled': !inputText || charCount > maxChars || !selectedVoice }]"
      >
        <span v-if="!isConverting">🎯 {{ $t('app.convert') }}</span>
        <span v-else>
          <span class="spinner"></span> {{ $t('app.converting') }}
        </span>
      </button>
      

    </div>
    
    <div v-if="audioUrl" class="audio-output gamified-card">
      <h3>🎧 {{ $t('app.generatedAudio') }}</h3>
      <div class="audio-player-container">
        <audio controls :src="audioUrl" class="gamified-audio"></audio>
      </div>
      <div class="download-options">
        <button @click="downloadAudio" class="download-btn" :disabled="isDownloading">
          <span v-if="!isDownloading">📥 {{ $t('app.download') }}</span>
          <span v-else>⏳ {{ $t('app.generating') }}</span>
        </button>
        <select v-model="downloadFormat" class="download-format-select">
          <option value="wav">Descargar como .wav</option>
          <option value="mp3">Descargar como .mp3</option>
        </select>
      </div>
      <div v-if="downloadError" class="error-message">
        ❌ {{ $t('app.error') }}: {{ downloadError }}
      </div>
      <div v-if="downloadFormat === 'mp3'" class="format-warning">
        ⚠️ {{ $t('app.mp3Warning') }}
      </div>
    </div>
    
    <AdBanner v-if="isFreeUser" :adSlot="'1234567890'"  :isPremiumUser="!isFreeUser"   />

    <SupportSection />
  </div>
</template>

<script>
import axios from 'axios'
import { useStore } from 'vuex'
import AdBanner from './AdBanner.vue'
import SupportSection from './SupportSection.vue'

export default {
  name: 'AdvancedTextToSpeech',
  components: {
    AdBanner,
    SupportSection 
  },
  data() {
    return {
      api_url: process.env.NODE_ENV === 'development'
        ? 'http://localhost/proyectos/spoket/'
        : 'model/',
      currentLocale: this.$i18n.locale,
      inputText: 'Hola, como has estado?',
      showAdvanced: false,
      selectedVoice: null,
      selectedLanguage: this.$i18n.locale,
      availableVoices: [],
      filteredVoices: [],
      rate: 1,
      volume: 1,
      pitch: 1,
      paragraphPause: 500,
      audioUrl: '',
      isConverting: false,
      isDownloading: false,
      downloadError: '',
      maxCharsFree: 1000,
      listenMode: false,
      currentBGM: null,
      audioFormats: ['mp3', 'wav'],
      selectedFormat: 'mp3',
      downloadFormat: 'mp3',
      mp3Quality: 'standard',
      mp3Qualities: [
        { value: 'standard', label: 'Calidad Estándar (tamaño pequeño, síntesis rápida)' },
        { value: 'high', label: 'Alta Calidad (tamaño medio, buena calidad)' },
        { value: 'premium', label: 'Calidad Premium (máxima calidad, tamaño grande)' }
      ],
      isDarkMode: true,
      audioBlobRef: null,
      availableLanguages: [
        { code: 'es', name: 'Español', nativeName: 'Español' },
        { code: 'en', name: 'Inglés', nativeName: 'English' },
        { code: 'fr', name: 'Francés', nativeName: 'Français' },
        { code: 'de', name: 'Alemán', nativeName: 'Deutsch' },
        { code: 'it', name: 'Italiano', nativeName: 'Italiano' },
        { code: 'pt', name: 'Portugués', nativeName: 'Português' },
        { code: 'ja', name: 'Japonés', nativeName: '日本語' },
        { code: 'zh', name: 'Chino', nativeName: '中文' },
        { code: 'ru', name: 'Ruso', nativeName: 'Русский' },
        { code: 'ar', name: 'Árabe', nativeName: 'العربية' }
      ]
    }
  },
  computed: {
    store() {
      return useStore()
    },
    maxChars() {
      return this.store.state.user.subscription === 'premium' ? 10000 : this.maxCharsFree
    },
    charCount() {
      return this.inputText.length
    },
    isFreeUser() {
      return this.store.state.user.subscription === 'free'
    },
    progressColor() {
      const percentage = (this.charCount / this.maxChars) * 100
      if (percentage < 60) return '#4CAF50'
      if (percentage < 90) return '#FFC107'
      return '#F44336'
    }
  },
  mounted() {
    // Cargar las voces disponibles
    this.loadVoices();
    
    // Aplicar modo oscuro por defecto
    document.documentElement.setAttribute('data-theme', 'dark')
  },
  methods: {
    loadVoices() {
      this.availableVoices = speechSynthesis.getVoices();
      
      if (this.availableVoices.length === 0) {
        speechSynthesis.onvoiceschanged = () => {
          this.availableVoices = speechSynthesis.getVoices();
          this.filterVoicesByLanguage();
        };
      } else {
        this.filterVoicesByLanguage();
      }
    },
    toggleAdvanced() {
      this.showAdvanced = !this.showAdvanced
    },
    // Filtrar voces por idioma seleccionado
    filterVoicesByLanguage() {
      if (this.availableVoices.length === 0) return
      
      this.filteredVoices = this.availableVoices.filter(voice => {
        return voice.lang.startsWith(this.selectedLanguage)
      })
      
      // Si hay voces disponibles, seleccionar la primera
      if (this.filteredVoices.length > 0) {
        this.selectedVoice = this.filteredVoices[0]
      } else {
        this.selectedVoice = null
      }
    },
    // Reproducir muestra de voz
    playVoiceSample() {
      if (!this.selectedVoice) return
      
      // Detener cualquier voz que se esté reproduciendo
      speechSynthesis.cancel()
      
      const sampleText = this.selectedLanguage === 'es' 
        ? 'Hola, esta es una muestra de mi voz.' 
        : 'Hello, this is a sample of my voice.'
      
      const utterance = new SpeechSynthesisUtterance(sampleText)
      utterance.voice = this.selectedVoice
      utterance.rate = this.rate
      utterance.volume = this.volume
      utterance.pitch = this.pitch
      
      speechSynthesis.speak(utterance)
    },


async convertToSpeech() {
  const textToConvert = this.listenMode 
    ? this.inputText.substring(0, 50) + (this.inputText.length > 50 ? '...' : '')
    : this.inputText;

  if (!textToConvert || textToConvert.length > this.maxChars || !this.selectedVoice) return;

  this.isConverting = true;
  this.downloadError = '';

  try {
    // Primero verificar si el servidor está disponible
    const serverAvailable = await this.checkServerHealth();

    console.log("server status: ", serverAvailable)
    
    let response;
    if (serverAvailable) {
      // Usar servidor PHP
      response = await this.generateAudioBlob(textToConvert);
    } else {
      // Fallback a Web Speech API
      console.log('Usando Web Speech API como fallback');
      const blob = await this.synthesizeWithWebSpeech(textToConvert);
      this.audioBlobRef = blob;
      this.audioUrl = URL.createObjectURL(blob);
      response = { success: true, url: this.audioUrl };
    }
    
    if (response && response.url) {
      this.audioUrl = response.url;
      
      // Si tenemos BGM, mezclar
      if (this.currentBGM && serverAvailable) {
        await this.mixAudioWithBGM();
      }
      
      // Reproducir el audio
      this.playAudio(this.audioUrl);
      
      // Registrar uso
      if (this.store.state.user.isLoggedIn) {
        this.store.dispatch('incrementUsage', textToConvert.length);
      }
    }
  } catch (error) {
    console.error('Error generando audio:', error);
    this.downloadError = 'Error al generar el audio: ' + error.message;
    
    // Último fallback: audio de muestra
    this.simulateAudioUrl();
  } finally {
    this.isConverting = false;
  }
},

// Verificar estado del servidor
async checkServerHealth() {
  try {
    const formData = new FormData();
    formData.append('action', 'health');

    const response = await axios.post(this.api_url + 'api.php', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    console.log("Response health: ", response);
    return response.data.status === 'OK';
  } catch (error) {
    console.warn('Servidor PHP no disponible, usando fallback local');
    return false;
  }
},

// Limpiar archivos temporales
async cleanTempFiles() {
  try {
    const response = await axios.post(this.api_url + 'api.php', {
      action: 'clean-files'
    });
    console.log('Archivos limpiados:', response.data);
  } catch (error) {
    console.warn('No se pudieron limpiar archivos temporales');
  }
},

// Método actualizado para generar audio
async generateAudioBlob(text) {
  try {
    console.log("Generando audio para texto:", text.substring(0, 50) + "...");
    console.log("Idioma seleccionado:", this.selectedVoice?.lang || 'en');

    const response = await axios.post(this.api_url + 'api.php', {
      action: 'text-to-speech',
      text: text,
      lang: this.selectedVoice?.lang || 'en',
      format: this.selectedFormat
    }, {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

    if (response.data.success) {
      return response.data;
    } else {
      throw new Error(response.data.error || 'No se pudo generar el audio');
    }
  } catch (error) {
    console.error('Error en generateAudioBlob:', error);
    
    // Fallback: usar Web Speech API si el servidor falla
    if (this.selectedVoice && speechSynthesis) {
      console.log('Usando fallback de Web Speech API');
      return await this.synthesizeWithWebSpeech(text);
    }
    
    throw error;
  }
},

playAudio(url) {
  const audio = new Audio(url);
  audio.play().catch(err => console.error('Error al reproducir:', err));
},

        // Reproducir el texto usando Web Speech API
    playText(text) {
      // Detener cualquier voz que se esté reproduciendo
      speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.voice = this.selectedVoice;
      utterance.rate = this.rate;
      utterance.volume = this.volume;
      utterance.pitch = this.pitch;
      
      // Evento cuando termina de hablar
      utterance.onend = () => {
        console.log("Speech synthesis completed");
      };
      
      // Evento para errores
      utterance.onerror = (event) => {
        console.error("Speech synthesis error", event);
      };
      
      speechSynthesis.speak(utterance);
    },


        // SIMULAR audio para demostración (sin backend)
    simulateAudioUrl() {
      // Usamos un audio de muestra para demostración
      this.audioUrl = 'https://audio-samples.github.io/samples/mp3/blizzard_biased/sample-1.mp3';
      
      // Creamos un blob vacío para la descarga (solo demo)
      this.audioBlobRef = new Blob([], { type: 'audio/mp3' });
    },
    
// Descargar audio - MÉTODO ACTUALIZADO
async downloadAudio() {
  if (!this.audioUrl) return;
  
  this.isDownloading = true;
  this.downloadError = '';
  
  try {
    // Si es una URL local, descargar normalmente
    if (this.audioUrl.startsWith('http')) {
      const response = await fetch(this.audioUrl);
      if (!response.ok) throw new Error('No se pudo descargar el archivo');
      
      const blob = await response.blob();
      this.downloadBlob(blob, `audio-spoket.${this.downloadFormat}`);
    } else {
      // Si es un blob local (fallback)
      this.downloadBlob(this.audioBlobRef, `audio-spoket.${this.downloadFormat}`);
    }
  } catch (error) {
    console.error('Error descargando audio:', error);
    this.downloadError = 'Error al descargar el audio: ' + error.message;
  } finally {
    this.isDownloading = false;
  }
},

downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
},

arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
},

    // Sintetizar usando Web Speech API
    async synthesizeWithWebSpeech(text) {
    return new Promise((resolve, reject) => {
        try {
        // Crear utterance
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.voice = this.selectedVoice;
        utterance.rate = this.rate;
        utterance.volume = this.volume;
        utterance.pitch = this.pitch;
        
        // Intentar capturar el audio usando Web Audio API
        if (this.supportsAudioCapture()) {
            console.log("supportAudioCapture")
            this.captureAudioWithWebAudio(utterance)
            .then(resolve)
            .catch(error => {
                console.warn('Captura Web Audio falló, usando método alternativo', error);
                this.useFallbackAudioCapture(utterance, resolve, reject);
            });
        } else {
            this.useFallbackAudioCapture(utterance, resolve, reject);
        }
        
        } catch (error) {
        reject(error);
        }
    });
    },

    // Capturar audio usando Web Audio API (método avanzado)
    async captureAudioWithWebAudio(utterance) {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const destination = audioContext.createMediaStreamDestination();
    const mediaRecorder = new MediaRecorder(destination.stream);
    const audioChunks = [];
    
    return new Promise((resolve, reject) => {
        mediaRecorder.ondataavailable = (event) => {
        audioChunks.push(event.data);
        };
        
        mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunks, { type: 'audio/wav' });
        resolve(audioBlob);
        };
        
        mediaRecorder.onerror = (event) => {
        reject(new Error('Error grabando audio: ' + event.error));
        };
        
        // Conectar la síntesis de voz al recorder (esto es experimental)
        // Nota: Esta aproximación puede no funcionar en todos los navegadores
        try {
        // Crear un gain node para controlar el volumen
        const gainNode = audioContext.createGain();
        gainNode.gain.value = this.volume;
        
        // Conectar mediante un oscilador silencioso (workaround)
        const oscillator = audioContext.createOscillator();
        oscillator.frequency.value = 0;
        oscillator.connect(gainNode);
        gainNode.connect(destination);
        oscillator.start();
        
        mediaRecorder.start();
        
        utterance.onend = () => {
            setTimeout(() => {
            mediaRecorder.stop();
            oscillator.stop();
            }, 100);
        };
        
        speechSynthesis.speak(utterance);
        
        } catch (error) {
        reject(error);
        }
    });
    },

    // Método alternativo cuando la captura Web Audio no está disponible
    useFallbackAudioCapture(utterance, resolve, reject) {
        // Usar un approach diferente: grabar el audio desde los altavoces
        // Esto requiere permisos del usuario pero es más confiable
        
        navigator.mediaDevices.getUserMedia({ audio: true })
            .then(stream => {
            const mediaRecorder = new MediaRecorder(stream);
            const audioChunks = [];
            
            mediaRecorder.ondataavailable = (event) => {
                audioChunks.push(event.data);
            };
            
            mediaRecorder.onstop = () => {
                const audioBlob = new Blob(audioChunks, { type: 'audio/wav' });
                // Detener todas las pistas del stream
                stream.getTracks().forEach(track => track.stop());
                resolve(audioBlob);
            };
            
            mediaRecorder.start();
            
            utterance.onend = () => {
                setTimeout(() => {
                mediaRecorder.stop();
                }, 500);
            };
            
            utterance.onerror = (event) => {
                stream.getTracks().forEach(track => track.stop());
                reject(new Error('Error en síntesis de voz: ' + event.error));
            };
            
            speechSynthesis.speak(utterance);
            })
            .catch(error => {
            console.warn('No se pudo acceder al micrófono, usando simulación', error);
            // Último recurso: simulación
            this.useAudioSimulation(utterance, resolve, reject);
            });
    },

    // Simulación de audio como último recurso
    useAudioSimulation(utterance, resolve, reject) {
        utterance.onend = () => {
            // Crear audio realista usando la Web Audio API
            this.createRealisticAudio(utterance)
            .then(resolve)
            .catch(error => {
                console.error('Error creando audio simulado:', error);
                // Fallback extremo: audio de muestra
                fetch('https://audio-samples.github.io/samples/mp3/blizzard_biased/sample-1.mp3')
                .then(response => response.blob())
                .then(resolve)
                .catch(reject);
            });
        };
        
        utterance.onerror = (event) => {
            reject(new Error('Error en síntesis de voz: ' + event.error));
        };
        
        speechSynthesis.speak(utterance);
    },

    // Crear audio realista usando Web Audio API
    async createRealisticAudio(utterance) {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const duration = this.estimateSpeechDuration(utterance);
        
        // Crear un buffer de audio
        const sampleRate = audioContext.sampleRate;
        const frameCount = sampleRate * duration;
        const audioBuffer = audioContext.createBuffer(1, frameCount, sampleRate);
        const data = audioBuffer.getChannelData(0);
        
        // Generar waveform de voz más realista
        for (let i = 0; i < frameCount; i++) {
            // Simular waveform de voz humana con múltiples frecuencias
            const time = i / sampleRate;
            const fundamentalFreq = 120 * Math.pow(2, (this.pitch - 1) * 12 / 12);
            
            // Onda compleja que simula voz
            let sample = 0;
            for (let harmonic = 1; harmonic <= 5; harmonic++) {
            const freq = fundamentalFreq * harmonic;
            const amplitude = 0.7 / harmonic;
            sample += amplitude * Math.sin(2 * Math.PI * freq * time);
            }
            
            // Añadir algo de ruido para naturalidad
            sample += (Math.random() - 0.5) * 0.1;
            
            // Aplicar envelope ADSR
            const attack = 0.05;
            const release = 0.2;
            let envelope = 1.0;
            
            if (time < attack) {
            envelope = time / attack;
            } else if (time > duration - release) {
            envelope = (duration - time) / release;
            }
            
            data[i] = sample * envelope * this.volume;
        }
        
        // Convertir a blob
        return new Promise((resolve) => {
            audioContext.encodeAudioData(audioBuffer)
            .then((arrayBuffer) => {
                const blob = new Blob([arrayBuffer], { type: 'audio/wav' });
                resolve(blob);
            })
            .catch(() => {
                // Fallback si encodeAudioData no está disponible
                const wavBuffer = this.audioBufferToWav(audioBuffer);
                const blob = new Blob([wavBuffer], { type: 'audio/wav' });
                resolve(blob);
            });
        });
    },

    // Estimar duración del speech
    estimateSpeechDuration(utterance) {
        const text = utterance.text;
        const words = text.split(/\s+/).length;
        const rate = utterance.rate;
        const wordsPerMinute = 150 * rate; // 150 WPM es habla normal
        return (words / wordsPerMinute) * 60 + 0.5; // +0.5s margen
    },

    // Convertir AudioBuffer a WAV
    audioBufferToWav(buffer) {
        const numChannels = buffer.numberOfChannels;
        const sampleRate = buffer.sampleRate;
        const length = buffer.length * numChannels * 2; // 2 bytes per sample
        const wavBuffer = new ArrayBuffer(44 + length);
        const view = new DataView(wavBuffer);
        
        // Write WAV header
        this.writeString(view, 0, 'RIFF');
        view.setUint32(4, 36 + length, true);
        this.writeString(view, 8, 'WAVE');
        this.writeString(view, 12, 'fmt ');
        view.setUint32(16, 16, true);
        view.setUint16(20, 1, true);
        view.setUint16(22, numChannels, true);
        view.setUint32(24, sampleRate, true);
        view.setUint32(28, sampleRate * numChannels * 2, true);
        view.setUint16(32, numChannels * 2, true);
        view.setUint16(34, 16, true);
        this.writeString(view, 36, 'data');
        view.setUint32(40, length, true);
        
        // Write audio data
        let offset = 44;
        for (let i = 0; i < buffer.length; i++) {
            for (let channel = 0; channel < numChannels; channel++) {
            const sample = Math.max(-1, Math.min(1, buffer.getChannelData(channel)[i]));
            view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
            offset += 2;
            }
        }
        
        return wavBuffer;
    },

    // Verificar si el navegador soporta captura de audio
    supportsAudioCapture() {
        return !!(window.MediaRecorder && (window.AudioContext || window.webkitAudioContext));
    },

    // Generar audio de muestra (simulación)
    generateSampleAudio() {
        // Esto es una simulación - en producción, capturarías el audio real
        // de la Web Speech API usando la Web Audio API
        const length = 44100 * 2; // 2 segundos de audio
        const buffer = new ArrayBuffer(44 + length * 2);
        const view = new DataView(buffer);
        
        // Escribir encabezado WAV
        this.writeString(view, 0, 'RIFF');
        view.setUint32(4, 36 + length * 2, true);
        this.writeString(view, 8, 'WAVE');
        this.writeString(view, 12, 'fmt ');
        view.setUint32(16, 16, true);
        view.setUint16(20, 1, true);
        view.setUint16(22, 1, true);
        view.setUint32(24, 44100, true);
        view.setUint32(28, 44100 * 2, true);
        view.setUint16(32, 2, true);
        view.setUint16(34, 16, true);
        this.writeString(view, 36, 'data');
        view.setUint32(40, length * 2, true);
        
        // Escribir datos de audio simples (silbido)
        let offset = 44;
        for (let i = 0; i < length; i++) {
            const sample = Math.sin(i * 0.1) * 0x7FFF;
            view.setInt16(offset, sample, true);
            offset += 2;
        }
        
        return buffer;
    },

    // Helper para escribir strings en el buffer
    writeString(view, offset, string) {
        for (let i = 0; i < string.length; i++) {
            view.setUint8(offset + i, string.charCodeAt(i));
        }
    },

    
    // Convertir WAV a MP3 en el cliente
    async convertToMp3(wavBlob) {
      return new Promise((resolve, reject) => {
        try {
          const reader = new FileReader();
          
          reader.onload = (e) => {
            try {
              const buffer = e.target.result;
              const wav = this.decodeWav(buffer);
              const mp3Data = this.encodeMp3(wav);
              
              const mp3Blob = new Blob(mp3Data, { type: 'audio/mp3' });
              resolve(mp3Blob);
            } catch (error) {
              reject(error);
            }
          };
          
          reader.onerror = (error) => {
            reject(error);
          };
          
          reader.readAsArrayBuffer(wavBlob);
        } catch (error) {
          reject(error);
        }
      });
    },

    // Estimar duración basada en texto
    estimateDuration(text) {
      const words = text.split(/\s+/).length;
      const wordsPerMinute = 150 * this.rate;
      return Math.max(1, (words / wordsPerMinute) * 60);
    },
    
    // Decodificar archivo WAV
    decodeWav(buffer) {
      const view = new DataView(buffer);
      
      // Leer encabezado WAV
      const riff = this.readString(view, 0, 4);
      const wave = this.readString(view, 8, 4);
      
      if (riff !== 'RIFF' || wave !== 'WAVE') {
        throw new Error('Formato de audio no válido');
      }
      
      // Buscar chunks
      let chunkOffset = 12;
      let formatChunk = null;
      let dataChunk = null;
      
      while (chunkOffset < view.byteLength - 8) {
        const chunkId = this.readString(view, chunkOffset, 4);
        const chunkSize = view.getUint32(chunkOffset + 4, true);
        
        if (chunkId === 'fmt ') {
          formatChunk = {
            offset: chunkOffset + 8,
            size: chunkSize
          };
        } else if (chunkId === 'data') {
          dataChunk = {
            offset: chunkOffset + 8,
            size: chunkSize
          };
          break;
        }
        
        chunkOffset += 8 + chunkSize;
      }
      
      if (!formatChunk || !dataChunk) {
        throw new Error('Estructura WAV no válida');
      }
      
      // Leer información del formato
      /* const audioFormat = view.getUint16(formatChunk.offset, true); */
      const numChannels = view.getUint16(formatChunk.offset + 2, true);
      const sampleRate = view.getUint32(formatChunk.offset + 4, true);
      const bitsPerSample = view.getUint16(formatChunk.offset + 14, true);
      
      // Leer datos de audio
      const length = dataChunk.size;
      const dataOffset = dataChunk.offset;
      
      return {
        sampleRate,
        channels: numChannels,
        bitsPerSample,
        data: new Int16Array(buffer, dataOffset, length / 2)
      };
    },
    
    // Codificar a MP3 usando lamejs
    encodeMp3(wav) {
      const { sampleRate, channels, data } = wav;
      
      // Importar lamejs dinámicamente para evitar errores de compilación
      const lamejs = require('lamejs');
      const mp3Encoder = new lamejs.Mp3Encoder(channels, sampleRate, 128);
      const sampleBlockSize = 1152;
      const mp3Data = [];
      
      for (let i = 0; i < data.length; i += sampleBlockSize) {
        const leftChunk = data.subarray(i, i + sampleBlockSize);
        let rightChunk;
        
        if (channels === 2) {
          rightChunk = data.subarray(i, i + sampleBlockSize);
        } else {
          // Mono - duplicar datos para ambos canales
          rightChunk = leftChunk;
        }
        
        const mp3Buffer = mp3Encoder.encodeBuffer(leftChunk, rightChunk);
        if (mp3Buffer.length > 0) {
          mp3Data.push(mp3Buffer);
        }
      }
      
      const finalBuffer = mp3Encoder.flush();
      if (finalBuffer.length > 0) {
        mp3Data.push(finalBuffer);
      }
      
      return mp3Data;
    },
    
    // Helper para leer strings del buffer
    readString(view, offset, length) {
      let result = '';
      for (let i = 0; i < length; i++) {
        result += String.fromCharCode(view.getUint8(offset + i));
      }
      return result;
    },
    
    updateCharCount() {
      if (this.inputText.length > this.maxChars) {
        this.inputText = this.inputText.substring(0, this.maxChars);
      }
    },
    // Subir BGM al backend
async handleBGMUpload(event) {
  const file = event.target.files[0];
  if (!file) return;
  
  try {
    const formData = new FormData();
    formData.append('bgm', file);
    
    const response = await axios.post(this.api_url + '?action=upload-bgm', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    if (response.data.success) {
      this.currentBGM = {
        name: response.data.originalName,
        filename: response.data.filename,
        url: response.data.url,
        data: response.data.data,
        size: response.data.size
      };
      alert('BGM subido correctamente');
    }
  } catch (error) {
    console.error('Error subiendo BGM:', error);
    alert('Error al subir el archivo BGM: ' + (error.response?.data?.error || error.message));
  }
},
    // Mezclar audio con BGM
async mixAudioWithBGM() {
  if (!this.audioBlobRef || !this.currentBGM) return;
  
  try {
    // Convertir blob a base64
    const audioArrayBuffer = await this.audioBlobRef.arrayBuffer();
    const audioBase64 = this.arrayBufferToBase64(audioArrayBuffer);
    
    const response = await axios.post(this.api_url + 'api.php', {
      action: 'mix-with-bgm',
      audioData: audioBase64,
      bgmData: this.currentBGM.data,
      volumeRatio: 0.7
    });
    
    if (response.data.success) {
      this.audioUrl = response.data.url;
      alert('Audio mezclado con BGM correctamente');
    }
  } catch (error) {
    console.error('Error mezclando audio:', error);
    alert('Error al mezclar audio con BGM: ' + (error.response?.data?.error || error.message));
  }
},
    playBGM() {
      if (this.currentBGM) {
        alert(`Reproduciendo BGM: ${this.currentBGM.name}`);
      }
    },
    stopBGM() {
      alert('Deteniendo BGM');
    },
    removeBGM() {
      this.currentBGM = null;
    },
    toggleTheme() {
      this.isDarkMode = !this.isDarkMode;
      document.documentElement.setAttribute('data-theme', this.isDarkMode ? 'dark' : 'light');
    }
  }
}
</script>


<style lang="scss">
@import '@/styles/variables.scss';

:root {
  --bg-primary: #121212;
  --bg-secondary: #1e1e1e;
  --bg-tertiary: #2d2d2d;
  --text-primary: #e0e0e0;
  --text-secondary: #a0a0a0;
  --accent-primary: #bb86fc;
  --accent-secondary: #03dac6;
  --accent-tertiary: #cf6679;
  --border-color: #383838;
  --card-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

[data-theme="light"] {
  --bg-primary: #f5f5f5;
  --bg-secondary: #ffffff;
  --bg-tertiary: #f0f0f0;
  --text-primary: #333333;
  --text-secondary: #666666;
  --accent-primary: #6200ee;
  --accent-secondary: #018786;
  --accent-tertiary: #b00020;
  --border-color: #e0e0e0;
  --card-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

* {
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
}

body {
  background-color: $dark-bg-primary;
  color: $dark-text-primary;
}

.tts-dark-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'Nunito Bold', sans-serif;
  background-color: $dark-bg-primary;
  min-height: 100vh;
}

.game-header {
  margin-bottom: 30px;
  background: linear-gradient(135deg, $dark-accent-primary, $accent-color);
  padding: 20px;
  border-radius: $border-radius-card;
  color: white;
  box-shadow: $dark-card-shadow;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 15px;
}

.game-header h2 {
  margin: 0;
  font-size: 1.8rem;
  font-weight: 700;
}

.level-badge {
  background: rgba(255, 255, 255, 0.2);
  padding: 8px 15px;
  border-radius: 20px;
  font-weight: bold;
  backdrop-filter: blur(5px);
}

.theme-toggle {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.2rem;
  cursor: pointer;
  backdrop-filter: blur(5px);
  transition: all 0.3s ease;
}

.theme-toggle:hover {
  transform: scale(1.1);
  background: rgba(255, 255, 255, 0.3);
}

.text-input-section {
  margin-bottom: 30px;
}

.input-container {
  position: relative;
  margin-bottom: 25px;
}

.gamified-textarea {
  width: 100%;
  height: 180px;
  padding: 20px;
  border: 2px solid $dark-border-color;
  border-radius: $border-radius-card;
  font-size: 16px;
  background: $dark-bg-secondary;
  color: $dark-text-primary;
  box-shadow: $dark-card-shadow;
  font-family: 'Nunito Bold', sans-serif;
  resize: none;
  transition: all 0.3s ease;
}

.gamified-textarea:focus {
  outline: none;
  border: $border-card;
  box-shadow: 0 0 0 3px rgba(134, 252, 169, 0.2);
}

.gamified-textarea::placeholder {
  color: $dark-text-secondary;
}

.char-counter-wrapper {
  position: absolute;
  bottom: 15px;
  right: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.char-counter {
  background: rgba(187, 134, 252, 0.1);
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 14px;
  color: $dark-accent-primary;
  font-weight: 600;
}

.progress-ring circle {
  transition: stroke-dashoffset 0.3s ease, stroke 0.3s ease;
}

.voice-selection-section {
  margin-bottom: 30px;
}

.voice-selection-section h3 {
  color: $dark-accent-primary;
  margin-bottom: 15px;
  font-size: 1.4rem;
  display: flex;
  align-items: center;
  gap: 10px;
}

.voice-controls {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 20px;
  margin-bottom: 20px;
}

.language-selector, .voice-selector {
  background: $dark-bg-secondary;
  border-radius: 12px;
  padding: 20px;
  box-shadow: $dark-card-shadow;
  border: 1px solid $dark-border-color;
}

.language-selector label, .voice-selector label {
  display: block;
  margin-bottom: 10px;
  font-weight: 600;
  color: $dark-accent-primary;
  display: flex;
  align-items: center;
  gap: 8px;
}

.gamified-select {
  width: 100%;
  padding: 12px 15px;
  border: 2px solid $dark-border-color;
  border-radius: 10px;
  background: $dark-bg-tertiary;
  font-size: 16px;
  color: $dark-text-primary;
  font-family: 'Nunito Bold', sans-serif;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23bb86fc' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 15px center;
  background-size: 16px;
}

.gamified-select:focus {
  outline: none;
  border-color: $dark-accent-primary;
}

.sample-btn {
  margin-top: 15px;
  width: 100%;
  padding: 10px;
  background: $dark-accent-tertiary;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
}

.sample-btn:hover:not(:disabled) {
  background: #b00020;
  transform: translateY(-2px);
}

.sample-btn:disabled {
  background: $dark-text-secondary;
  cursor: not-allowed;
}

.voice-preview {
  background: $dark-bg-secondary;
  border-radius: 12px;
  padding: 15px;
  box-shadow: $dark-card-shadow;
  border: 1px solid $dark-border-color;
}

.voice-details h4 {
  margin: 0 0 5px 0;
  color: $dark-accent-primary;
}

.voice-details p {
  margin: 0;
  color: $dark-text-secondary;
  font-size: 0.9rem;
}

.advanced-controls {
  margin-bottom: 30px;
}

.advanced-controls h3 {
  color: $dark-accent-primary;
  margin-bottom: 20px;
  font-size: 1.4rem;
  display: flex;
  align-items: center;
  gap: 10px;
}

.controls-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.control-column {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.control-card {
  background: $dark-bg-secondary;
  border-radius: $border-radius-card;
  padding: 20px;
  box-shadow: $dark-card-shadow;
  border: 1px solid $dark-border-color;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.control-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
}

.control-card h4 {
  color: $dark-accent-primary;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.1rem;
}

.control-description {
  color: $dark-text-secondary;
  font-size: 0.9rem;
  margin-bottom: 15px;
  line-height: 1.4;
}

.toggle-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-switch .slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: $dark-text-secondary;
  transition: .4s;
}

.toggle-switch .slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: .4s;
}

.toggle-switch input:checked + .slider {
  background-color: $dark-accent-primary;
}

.toggle-switch input:checked + .slider:before {
  transform: translateX(26px);
}

.toggle-switch .slider.round {
  border-radius: 24px;
}

.toggle-switch .slider.round:before {
  border-radius: 50%;
}

.toggle-label {
  font-weight: 600;
  color: $dark-accent-primary;
}

.bgm-upload-area, .bgm-management {
  text-align: center;
}

.upload-btn {
  display: inline-block;
  background: linear-gradient(to right, $dark-accent-primary, $accent-color);
  color: white;
  padding: 10px 20px;
  border-radius: 50px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
}

.upload-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(187, 134, 252, 0.3);
}

.bgm-management p {
  margin-bottom: 10px;
  font-weight: 600;
  color: $dark-text-primary;
}

.bgm-controls {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.bgm-btn {
  background: $dark-bg-tertiary;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: $dark-text-primary;
}

.bgm-btn:hover {
  background: $dark-accent-primary;
  transform: scale(1.1);
  color: white;
}

.format-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.format-btn {
  padding: 8px 5px;
  border: 2px solid $dark-border-color;
  border-radius: 8px;
  background: $dark-bg-tertiary;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  font-weight: 600;
  color: $dark-text-primary;
}

.format-btn:hover {
  border-color: $dark-accent-primary;
}

.format-btn.active {
  background: $dark-accent-primary;
  color: white;
  border-color: $dark-accent-primary;
}

.slider-container {
  margin-bottom: 15px;
}

.gamified-slider {
  -webkit-appearance: none;
  width: 100%;
  height: 8px;
  border-radius: 10px;
  background: linear-gradient(to right, $dark-accent-secondary, $dark-accent-primary);
  outline: none;
  margin-bottom: 5px;
}

.gamified-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: $dark-accent-primary;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

.slider-value {
  text-align: center;
  font-weight: 600;
  color: $dark-accent-primary;
  font-size: 0.9rem;
}

.convert-btn {
  background: linear-gradient(to right, $dark-accent-primary, $dark-accent-tertiary);
  color: white;
  border: none;
  padding: 16px 40px;
  font-size: 18px;
  font-weight: 700;
  border-radius: 50px;
  cursor: pointer;
  box-shadow: 0 5px 15px rgba(20, 156, 66, 0.4);
  transition: all 0.3s ease;
  display: block;
  margin: 0 auto;
  width: 100%;
  max-width: 300px;
  position: relative;
}

.convert-btn:hover:not(.disabled) {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(14, 168, 48, 0.6);
}

.convert-btn.converting {
  animation: pulse 1.5s infinite;
}

.convert-btn.disabled {
  background: $dark-text-secondary;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  margin-right: 8px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.free-user-notice {
  margin-top: 20px;
  margin-bottom: 20px;
  padding: 5px;
  background: linear-gradient(to right, #382c0b, #5e4b13);
  border: 1px solid #856404;
  border-radius: 10px;
  color: #ffd54f;
  text-align: center;
}

.free-user-notice a {
  color: #ffd54f;
  text-decoration: underline;
}

.gamified-card {
  background: $dark-bg-secondary;
  border-radius: $border-radius-card;
  padding: 25px;
  box-shadow: $dark-card-shadow;
  text-align: center;
  animation: fadeIn 0.5s ease;
  border: 1px solid $dark-border-color;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.audio-output h3 {
  color: $dark-accent-primary;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.audio-player-container {
  margin-bottom: 20px;
}

.gamified-audio {
  width: 100%;
  border-radius: 10px;
  background: $dark-bg-tertiary;
}

.download-options {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
}

.download-btn {
  background: $dark-accent-secondary;
  color: white;
  border: none;
  padding: 12px 25px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.download-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(3, 218, 198, 0.4);
}

.download-format-select {
  padding: 10px 15px;
  border: 2px solid $dark-accent-primary;
  border-radius: 10px;
  background: $dark-bg-tertiary;
  font-size: 14px;
  color: $dark-text-primary;
}

@media (max-width: 968px) {
  .controls-grid {
    grid-template-columns: 1fr;
  }
  
  .voice-controls {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }
  
  .game-header h2 {
    font-size: 1.5rem;
  }
  
  .download-options {
    flex-direction: column;
  }
  
  .control-card {
    padding: 15px;
  }
}

.download-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.error-message {
  margin-top: 15px;
  padding: 10px;
  background-color: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  border-radius: 8px;
  color: #f44336;
  font-size: 0.9rem;
}

.format-warning {
  margin-top: 10px;
  padding: 8px;
  background-color: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 8px;
  color: #ffc107;
  font-size: 0.85rem;
  text-align: center;
}

.advanced-controls {
  margin-bottom: 30px;
  border: 1px solid $dark-border-color;
  border-radius: 12px;
  overflow: hidden;
  background: $dark-bg-secondary;
}

.advanced-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  cursor: pointer;
  background: $dark-bg-tertiary;
  transition: background-color 0.3s ease;
}

.advanced-header:hover {
  background: rgba(187, 134, 252, 0.1);
}

.advanced-header h3 {
  margin: 0;
  color: $dark-accent-primary;
  font-size: 1.4rem;
  display: flex;
  align-items: center;
  gap: 10px;
}

.toggle-icon {
  font-size: 1.2rem;
  color: $dark-accent-primary;
  transition: transform 0.3s ease;
}

.advanced-content {
  padding: 20px;
}

/* Transición para el despliegue */
.slide-enter-active, .slide-leave-active {
  transition: all 0.3s ease;
  max-height: 1000px;
  overflow: hidden;
}

.slide-enter-from, .slide-leave-to {
  max-height: 0;
  opacity: 0;
  padding: 0 20px;
}
</style>